import { useState } from "react";

import { MyCustomer } from "models/customers/customers";
import { SortOrder } from "common/SortOrder";
import { Icon, SortIcon } from "components/icon/Icon";
import { Loader } from "components/loader/Loader";
import Link from "components/link/Link";
import { PrimaryArrowButton } from "components/button/Button";
import CustomersHeader from "views/customers/components/CustomersHeader";
import { useRootStore } from "store";

export default function AllCustomers() {
  const { searchForCustomers, allCustomers } = useRootStore(
    (state) => state.customers
  );
  const { customers, isLoading } = allCustomers;

  const SortCustomerId = (customer: MyCustomer) => customer.customerId;
  const SortCustomerOrganizationNumber = (customer: MyCustomer) =>
    customer.organizationNumber;
  const SortCustomerName = (customer: MyCustomer) => customer.customerName;
  const SortCustomerSalesPerson = (customer: MyCustomer) =>
    customer.salesPerson.givenName;
  const SortCustomerIsPrivate = (customer: MyCustomer) => customer.isPrivate;

  const [searchTerm, setSearchTerm] = useState("");
  const [openingCustomerId, setOpeningCustomerId] = useState("");
  const [sortOrder, setSortOrder] = useState(SortOrder.Ascending);
  const [sortBy, setSortBy] = useState("");

  const searchCustomers = (searchParam: string) => {
    if (searchParam.length > 0) {
     searchForCustomers(searchParam);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Start search when pressing Enter key
    if (e.key === "Enter") {
      searchCustomers(searchTerm);
    }
  };

  const setSorting = (newSortBy: string) => {
    const sortingOrder =
      sortBy === newSortBy && sortOrder === SortOrder.Ascending
        ? SortOrder.Descending
        : SortOrder.Ascending;
    setSortOrder(sortingOrder);
    setSortBy(newSortBy);
  };

  const sortTable = (customerA: MyCustomer, customerB: MyCustomer) => {
    let sortFunction: (customer: MyCustomer) => boolean | string =
      SortCustomerName;

    switch (sortBy) {
      case "SortCustomerId":
        sortFunction = SortCustomerId;
        break;
      case "SortCustomerOrganizationNumber":
        sortFunction = SortCustomerOrganizationNumber;
        break;
      case "SortCustomerSalesPerson":
        sortFunction = SortCustomerSalesPerson;
        break;
      case "SortCustomerIsPrivate":
        sortFunction = SortCustomerIsPrivate;
        break;
      default:
        sortFunction = SortCustomerName;
        break;
    }

    if (sortOrder === SortOrder.Descending) {
      return sortFunction(customerA) > sortFunction(customerB) ? -1 : 1;
    }
    return sortFunction(customerA) > sortFunction(customerB) ? 1 : -1;
  };

  const addToCustomerButton = (customer: MyCustomer) => {
    if (customer.requestedAddToCustomer) {
      return "Begäran skickad";
    }

    if (
      customer.state === "Active" ||
      customer.state === "ReactivatedInMaconomy"
    ) {
      const alert = (
        <span>
          Du är på väg in på en kund som du inte har i ditt kundteam.
          <ul>
            <li>Jag bekräftar att jag har rätten att lägga till mig kunden.</li>
            <li>Jag är medveten om att åtgärden loggas.</li>
          </ul>
          <div style={{ width: "100%" }}>
            <Link
              style={{ width: "50%", display: "inline-block" }}
              to={`/customers/${customer.customerId}/dashboard`}
            >
              Klicka för att gå till kund
            </Link>
            <span
              style={{ width: "50%", display: "inline-block" }}
              onClick={() => {
                setOpeningCustomerId("");
              }}
            >
              Avbryt <Icon name="close" title="Avbryt" />
            </span>
          </div>
        </span>
      );
      return (
        (openingCustomerId === customer.customerId && alert) || (
          <PrimaryArrowButton
            size="sm"
            onClick={() => {
              setOpeningCustomerId(customer.customerId);
            }}
          >
            Visa kund
          </PrimaryArrowButton>
        )
      );
    }

    return "Kund är ej aktiv i MittKontor";
  };

  return (
    <div className={"box"}>
      <CustomersHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleSearch={searchCustomers}
        handleKeyUp={handleKeyUp}
        isAllCustomers
      />

      <div className="table-wrapper w-100">
        <table className="w-100 min-w-400">
          <thead>
            <tr>
              <th
                className="cursor-pointer"
                onClick={() => setSorting("SortCustomerName")}
              >
                <div className="d-flex">
                  Kund
                  <SortIcon
                    className="ml-2"
                    sortOrder={sortBy === "SortCustomerName" ? sortOrder : ""}
                  />
                </div>
              </th>
              <th
                className="cursor-pointer"
                onClick={() => setSorting("SortCustomerId")}
              >
                <div className="d-flex">
                  Kundnummer
                  <SortIcon
                    className="ml-2"
                    sortOrder={sortBy === "SortCustomerId" ? sortOrder : ""}
                  />
                </div>
              </th>
              <th
                className="cursor-pointer"
                onClick={() => setSorting("SortCustomerOrganizationNumber")}
              >
                <div className="d-flex">
                  Org. nummer{" "}
                  <SortIcon
                    className="ml-2"
                    sortOrder={
                      sortBy === "SortCustomerOrganizationNumber"
                        ? sortOrder
                        : ""
                    }
                  />
                </div>
              </th>
              <th
                className="cursor-pointer"
                onClick={() => setSorting("SortCustomerSalesPerson")}
              >
                <div className="d-flex">
                  Kundansvarig{" "}
                  <SortIcon
                    className="ml-2"
                    sortOrder={
                      sortBy === "SortCustomerSalesPerson" ? sortOrder : ""
                    }
                  />
                </div>
              </th>
              <th
                className="cursor-pointer"
                onClick={() => setSorting("SortCustomerIsPrivate")}
              >
                <div className="d-flex">
                  Sekretess{" "}
                  <SortIcon
                    className="ml-2"
                    sortOrder={
                      sortBy === "SortCustomerIsPrivate" ? sortOrder : ""
                    }
                  />
                </div>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {customers.sort(sortTable).map((customer) => {
              const lockedStatus = customer.isPrivate ? "Låst" : "Öppen";
              return (
                <tr key={customer.customerNumber}>
                  <td>{customer.customerName}</td>
                  <td>{customer.customerNumber}</td>
                  <td>{customer.organizationNumber}</td>
                  <td>
                    {customer.salesPerson
                      ? `${customer.salesPerson.givenName} ${customer.salesPerson.surName}`
                      : "-"}
                  </td>
                  <td>{lockedStatus}</td>
                  <td>{addToCustomerButton(customer)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!isLoading && customers.length === 0 && (
        <p>Inga resultat på sökningen.</p>
      )}
      {isLoading && <Loader className="p-4" />}
    </div>
  );
}
